// MiniInvoice: an invoice in short, where a History entry about it opens in place (Amir and Brook, 26
// Oct: "an invoice entry opens as a mini invoice"). Its number and where it stands on one line, its
// first lines (LineTable: what, how many, the amount), "and n more lines" when there are more, then its
// sums (Totals: the total, what's been paid, what's owing). The entry's own link opens the invoice
// itself. Reads only --miniinv-*.
import { LineTable } from '../LineTable/LineTable.tsx';
import { Totals, type TotalsRow } from '../Totals/Totals.tsx';
import './MiniInvoice.css';

export interface MiniInvoiceLine {
  id: string;
  item: string;
  qty: string;
  amount: string;
}

export interface MiniInvoiceView {
  /** "INV-18402" */
  number: string;
  /** Where it stands, in words: "Finalized", "Sent", "Paid in full", "Void". */
  status: string;
  /** The status is a problem (overdue, void, didn't send): said in red. */
  isBad?: boolean;
  /** Its first lines; none when the viewer may not see them (the totals still show). */
  lines: MiniInvoiceLine[];
  /** How many lines aren't shown. */
  more?: number;
  total: string;
  /** Taken off already: "−$500.00". */
  paid?: string;
  /** What's owing: "$3,322.00", or "$0.00". */
  owing: string;
}

export interface MiniInvoiceProps {
  invoice: MiniInvoiceView;
}

export function MiniInvoice({ invoice: v }: MiniInvoiceProps) {
  const rows: TotalsRow[] = [
    { label: 'Total', value: v.total, kind: 'strong' },
    ...(v.paid ? [{ label: 'Paid', value: v.paid }] : []),
    { label: 'Owing', value: v.owing, kind: 'due' },
  ];
  return (
    <div className="fl-miniinv" role="group" aria-label={`${v.number}, ${v.status}`}>
      <p className="fl-miniinv__head">
        <span className="fl-miniinv__number">{v.number}</span>
        <span className="fl-miniinv__state" data-bad={v.isBad ? '' : undefined}>
          {v.status}
        </span>
      </p>
      {v.lines.length ? (
        <LineTable
          label={`${v.number}’s lines`}
          columns={[
            { id: 'item', label: 'Line' },
            { id: 'qty', label: 'Qty', align: 'end' },
            { id: 'amount', label: 'Amount', align: 'end' },
          ]}
          rows={v.lines.map((l) => ({ id: l.id, cells: { item: l.item, qty: l.qty, amount: l.amount } }))}
        />
      ) : null}
      {v.more ? (
        <p className="fl-miniinv__more">
          and {v.more} more {v.more === 1 ? 'line' : 'lines'}
        </p>
      ) : null}
      <Totals label={`${v.number}’s totals`} rows={rows} />
    </div>
  );
}
